import React from 'react';
import { observer } from 'mobx-lite';
import {
    Stack, Typography, TextField, MenuItem, FormControlLabel, Switch,
    Box, IconButton, Button, Divider,
} from '@mui/material';
import { Add, Delete, RestartAlt } from '@mui/icons-material';
import { MainStoreContextItem } from '../../Stores/MainStore';

const DetectorList: React.FC<{
    label: string;
    items: string[];
    onChange: (items: string[]) => void;
}> = ({ label, items, onChange }) => {
    const update = (i: number, v: string) => {
        const next = [...items]; next[i] = v; onChange(next);
    };
    const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
    const add = () => onChange([...items, '']);
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
            <Stack spacing={1}>
                {items.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1 }}>
                        <TextField size="small" fullWidth value={item}
                            placeholder="Detector name"
                            onChange={(e) => update(i, e.target.value)} />
                        <IconButton onClick={() => remove(i)}><Delete fontSize="small" /></IconButton>
                    </Box>
                ))}
                <Button startIcon={<Add />} onClick={add} size="small" sx={{ alignSelf: 'flex-start' }}>
                    Add {label}
                </Button>
            </Stack>
        </Box>
    );
};

const GuardModeSettings: React.FC = observer(() => {
    const store = MainStoreContextItem.useStore();
    const s = store.settingsStore;

    return (
        <Stack spacing={3} sx={{ maxWidth: 560 }}>
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Guard Mode</Typography>
                <Typography variant="caption" color="text.secondary">
                    Provision this device and configure its detectors.
                </Typography>
            </Box>

            <TextField select label="App Mode" size="small" value={s.appMode}
                onChange={(e) => s.setAppMode(e.target.value as 'kiosk' | 'guard')}>
                <MenuItem value="kiosk">Kiosk Verify</MenuItem>
                <MenuItem value="guard">Guard</MenuItem>
            </TextField>

            <Divider />

            <TextField label="Neutral Detector" size="small" value={s.guardNeutralDetector}
                placeholder="Detector used for identify / theme lookup"
                onChange={(e) => s.setGuardNeutralDetector(e.target.value)} />

            <DetectorList label="IN Detector" items={s.guardInDetectors} onChange={s.setGuardInDetectors} />
            <DetectorList label="OUT Detector" items={s.guardOutDetectors} onChange={s.setGuardOutDetectors} />

            <Divider />

            <Box>
                <Typography variant="subtitle2">Person field mapping</Typography>
                <Typography variant="caption" color="text.secondary">
                    Match the entity field names configured on your Identities server.
                </Typography>
            </Box>
            <TextField label="Email field name" size="small" value={s.guardEmailField}
                onChange={(e) => s.setGuardEmailField(e.target.value)} />
            <TextField label="Phone field name" size="small" value={s.guardPhoneField}
                onChange={(e) => s.setGuardPhoneField(e.target.value)} />
            <TextField label="Date of birth field name" size="small" value={s.guardDobField}
                onChange={(e) => s.setGuardDobField(e.target.value)} />
            <TextField label="Vendor field name" size="small" value={s.guardVendorField}
                onChange={(e) => s.setGuardVendorField(e.target.value)} />
            <Button size="small" variant="outlined" startIcon={<RestartAlt />} sx={{ alignSelf: 'flex-start' }}
                onClick={() => s.resetGuardFieldMapping()}>
                Reset field names to defaults
            </Button>

            <FormControlLabel
                control={<Switch checked={s.guardSoundEnabled} onChange={(e) => s.setGuardSoundEnabled(e.target.checked)} />}
                label="Speech / sound on result"
            />

            <Box>
                <FormControlLabel
                    control={<Switch checked={s.guardShowIgnoreButton} onChange={(e) => s.setGuardShowIgnoreButton(e.target.checked)} />}
                    label="Show Ignore button on results"
                />
                <Typography variant="caption" color="text.secondary" component="div">
                    When off, the guard must record In or Out. Dismiss still appears when no
                    In/Out detector is configured.
                </Typography>
            </Box>
        </Stack>
    );
});

export default GuardModeSettings;
